import { Link } from 'react-router-dom';
import { ArrowRight, Calendar, Clock } from 'lucide-react';
import { BLOG_INDEX } from '@/data/blog';

/* Homepage teaser for the Heritage Estate Blog Series — surfaces the three
 * long-form articles above the Newsletter block. Muddy-trail Highland
 * texture background at low opacity keeps the section on-brand. */

const BG =
  'https://images.unsplash.com/photo-1666627389585-e526833bea8a?fm=jpg&q=60&w=2000&auto=format&fit=crop';

function formatDate(iso) {
  const d = new Date(iso);
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

export default function BlogSeriesTeaser() {
  return (
    <section
      id="heritage-blog"
      data-testid="heritage-blog-teaser"
      className="section relative overflow-hidden text-[var(--brand-parchment)]"
    >
      <img
        src={BG}
        alt=""
        aria-hidden="true"
        className="absolute inset-0 w-full h-full object-cover opacity-50"
      />
      <div
        aria-hidden="true"
        className="absolute inset-0"
        style={{
          background:
            'linear-gradient(180deg, rgba(14,61,20,0.85) 0%, rgba(14,61,20,0.72) 100%)',
        }}
      />
      <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-[1fr_2fr] gap-10 lg:gap-16 items-end">
          <div>
            <span className="font-eyebrow !text-[var(--brand-gold)]">Heritage Estate Blog Series</span>
            <h2 className="mt-3 font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.02]">
              Long-form thinking from <span className="italic text-[var(--brand-gold)]">the hill.</span>
            </h2>
          </div>
          <p className="text-[var(--brand-parchment)]/90 text-lg leading-relaxed max-w-lg">
            Three new articles on why we chose brass, why 250&thinsp;W and dual motors
            are the real proving grounds, and how step-through frames unlock the
            world for a much wider range of riders.
          </p>
        </div>

        <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {BLOG_INDEX.map((post, i) => (
            <Link
              to={`/blog/${post.slug}`}
              key={post.slug}
              data-testid={`teaser-blog-${post.slug}`}
              className="group flex flex-col bg-[var(--brand-parchment)] text-[var(--brand-forest)] hover:bg-[var(--brand-parchment)]/95 transition-colors"
            >
              <div className="aspect-[16/10] overflow-hidden">
                <img
                  src={post.heroImage}
                  alt={post.heroAlt}
                  loading="lazy"
                  className="w-full h-full object-cover transition-transform duration-[900ms] group-hover:scale-[1.04]"
                />
              </div>
              <div className="p-5 flex flex-col flex-1">
                <div className="flex items-center gap-3 text-[10px] uppercase tracking-[0.2em] text-[var(--brand-muted)]">
                  <span className="inline-flex items-center gap-1.5"><Calendar size={11} /> {formatDate(post.date)}</span>
                  <span className="inline-flex items-center gap-1.5"><Clock size={11} /> {post.readMins} min</span>
                </div>
                <h3 className="mt-3 font-display text-lg leading-tight group-hover:text-[var(--brand-gold-soft)] transition-colors">{i + 1}. {post.title}</h3>
                <p className="mt-2 text-sm text-[var(--brand-muted)] leading-relaxed line-clamp-3">{post.dek}</p>
                <span className="mt-4 inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.2em] text-[var(--brand-forest)]">
                  Read <ArrowRight size={12} />
                </span>
              </div>
            </Link>
          ))}
        </div>

        <div className="mt-10">
          <Link
            to="/blog"
            data-testid="teaser-see-all-blog"
            className="btn-gold inline-flex px-6 py-3 text-sm font-semibold tracking-wide items-center gap-2 rounded-none"
          >
            See the full series
          </Link>
        </div>
      </div>
    </section>
  );
}
